import {
  Box,
  Flex,
  type HTMLChakraProps,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { addYears } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCookie } from 'react-use';

import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';
import { Button, ButtonGroup } from '@revelio/ui';

import { useTabContentConfig } from '../../../../../components/PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../../../../components/PageConfigProvider/types';
import { isScreenerV2Page } from '../../../../../utils/routing-helpers';
import { aiFilterSearchRequest } from '../../../../ai-search/ai-search-api-request';
import {
  getRoleTaxonomyIdForAiSearch,
  getSkillTaxonomyIdFromActiveFilterMap,
  getTaxonomyAdaptiveForAiSearch,
} from '../../../../ai-search/getAiSearchTaxonomyFromFilterMap';
import { screenerAiSearchRequest } from '../../../../ai-search/screener-ai-search-api-request';
import { useApplyAiSearchFilters } from '../../../../ai-search/useApplyAiSearchFilters';
import { useAiSearchContext } from '../../../../contexts/AiSearchContext';
import { useFilterContext } from '../../../../contexts/FilterProvider';
import { useViewDateGranularity } from '../../../../hooks/useViewDateGranularity';
import { FilterId } from '../../../../types/filters';
import styles from './ai-search.module.css';
import { ResponseHighlightedOverlay } from './ResponseHighlightedOverlay';
import SpeechInput, { type SpeechInputHandle } from './SpeechInput';

interface AiSearchFilterFormProps {
  closeMenu: () => void;
  isOpen?: boolean;
}

// Page-scoped preference filters (taxonomy + data corrections) synced from
// saved prefs, not manual AI refinements. Ignored when deciding overlay
// visibility — otherwise their `isUserSubmitted: true` reads as "user touched
// the filters" and hides the highlighted response.
// SNAPSHOT_DATE is synced from DATE_RANGE on apply (syncDateFilterDependencies)
// on every page that carries both filters — a derived dependency, not a manual
// edit — so it belongs here too.
const AI_OVERLAY_IGNORED_FILTER_IDS = new Set<FilterId>([
  FilterId.ROLE_TAXONOMY,
  FilterId.SKILL_TAXONOMY,
  FilterId.DATA_ADJUSTMENTS,
  FilterId.CONTINGENT_WORKERS,
  FilterId.SNAPSHOT_DATE,
]);

const getPlaceholderText = (
  revelioPageId: RevelioPageId | undefined
): string => {
  switch (revelioPageId) {
    case RevelioPageId.TALENT_DISCOVERY:
      return 'e.g. Find me data scientists at OpenAI and their top competitors with skills in machine learning';
    case RevelioPageId.COMPANY_OVERVIEW_V2:
      return 'e.g. Software engineers in California with Python skills';
    default:
      return 'e.g. Describe the filters you want to apply';
  }
};

export const AiSearchFilterForm = ({
  closeMenu,
  isOpen = true,
}: AiSearchFilterFormProps) => {
  const {
    aiSearchState,
    setAiSearchState,
    isAiSearchLoading,
    setIsAiSearchLoading,
    abortControllerRef,
    aiGeneratedFilterIds,
    setAiGeneratedFilterIds,
    defaultAiSearchSet,
    setDefaultAiSearchSet,
  } = useAiSearchContext();

  const { activeFilterMap, filterState } = useFilterContext();
  const { revelioPageId } = useTabContentConfig();
  const viewGranularity = useViewDateGranularity();
  const { applyAiSearchFilters, applyScreenerAiSearchFilters } =
    useApplyAiSearchFilters();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const speechInputRef = useRef<SpeechInputHandle>(null);
  const [isFocused, setIsFocused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout>();

  const closeMenuAndStopRecording = useCallback(() => {
    speechInputRef.current?.stopRecording();
    closeMenu();
  }, [closeMenu]);

  useEffect(() => {
    if (!isOpen) {
      speechInputRef.current?.stopRecording();
    }
  }, [isOpen]);

  const handleTranscriptComplete = (transcript: string) => {
    setAiSearchState((prevState) => ({
      ...prevState,
      prompt: prevState.prompt
        ? `${prevState.prompt} ${transcript}`
        : transcript,
      response: null,
      uuid: '',
    }));
  };

  const handleSubmit = async () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setDefaultAiSearchSet(false);
    if (defaultAiSearchSet && aiSearchState.uuid) {
      setText('');
      setAiSearchState((prevState) => ({
        ...prevState,
        prompt: '',
        response: null,
        uuid: '',
      }));
      textAreaRef.current?.focus();
      setIsFocused(true);
      return;
    }
    abortControllerRef.current = new AbortController();

    try {
      setIsAiSearchLoading(true);
      closeMenuAndStopRecording();

      const isScreener = isScreenerV2Page(revelioPageId);

      if (isScreener) {
        const { response, uuid } = await screenerAiSearchRequest({
          prompt: aiSearchState.prompt,
          signal: abortControllerRef.current.signal,
        });
        const { adaptedFilters, unknownFilters: mergedUnknownFilters } =
          applyScreenerAiSearchFilters(response);
        setAiGeneratedFilterIds(adaptedFilters.map((filter) => filter.id));
        setAiSearchState((prevState) => ({
          ...prevState,
          uuid,
          isCorrect: null,
          response: {
            ...response,
            unknownFilters: mergedUnknownFilters.map((u) => ({
              name: u.name,
              relevantPromptText: u.relevantPromptText ?? u.name,
              explanation: u.explanation ?? '',
            })),
          },
        }));
        return;
      }

      const rolesTax = getRoleTaxonomyIdForAiSearch(activeFilterMap);
      const skillsTax = getSkillTaxonomyIdFromActiveFilterMap(activeFilterMap);
      const { rolesTaxAdaptive, skillsTaxAdaptive } =
        getTaxonomyAdaptiveForAiSearch({
          revelioPageId,
          primaryFilterId: filterState.activeKey,
        });
      const { response, uuid } = await aiFilterSearchRequest({
        prompt: aiSearchState.prompt,
        rolesTax,
        skillsTax,
        rolesTaxAdaptive,
        skillsTaxAdaptive,
        dateGranularity: viewGranularity,
        signal: abortControllerRef.current.signal,
      });

      const { adaptedFilters, unknownFilters: mergedUnknownFilters } =
        await applyAiSearchFilters(response);

      setAiGeneratedFilterIds(adaptedFilters.map((filter) => filter.id));

      setAiSearchState((prevState) => ({
        ...prevState,
        uuid,
        isCorrect: null,
        response: {
          ...response,
          unknownFilters: mergedUnknownFilters,
        },
      }));
    } catch (error) {
      // Only log error if it's not an abort error
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error(error);
      }
    } finally {
      setIsAiSearchLoading(false);
      abortControllerRef.current = undefined;
    }
  };

  // Check if AI-generated filters are still untouched (no manual changes)
  const aiGeneratedFilterSet = new Set(aiGeneratedFilterIds);
  const hasNonAiUserSubmittedFilters = Array.from(
    activeFilterMap.values()
  ).some(
    (filter) =>
      Boolean(filter.isUserSubmitted) &&
      !aiGeneratedFilterSet.has(filter.id) &&
      !AI_OVERLAY_IGNORED_FILTER_IDS.has(filter.id)
  );
  const aiFiltersPresent = aiGeneratedFilterIds.every((id) =>
    activeFilterMap.has(id)
  );
  const areAiFiltersUntouched =
    aiGeneratedFilterIds.length > 0 &&
    aiFiltersPresent &&
    !hasNonAiUserSubmittedFilters;

  const shouldShowOverlay = areAiFiltersUntouched || defaultAiSearchSet;

  const showingHighlightedOverlay =
    aiSearchState.response &&
    !isFocused &&
    shouldShowOverlay &&
    'expr' in aiSearchState.response.filters;

  useEffect(() => {
    if (!showingHighlightedOverlay && textAreaRef.current) {
      textAreaRef.current.focus();
    }
  }, [showingHighlightedOverlay]);

  const fullText = aiSearchState.prompt;
  const [text, setText] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [aiWriterDone, updateAiWriterDone] = useCookie('ai-writer');

  useEffect(() => {
    if (aiWriterDone !== null || !defaultAiSearchSet) {
      setText(fullText);
      return;
    }

    const initialDelay = 250;
    const writeSpeed = 50;
    setTimeout(() => {
      setIsAnimating(true);
      let index = 0;
      intervalRef.current = setInterval(() => {
        if (index < fullText.length) {
          setText(fullText.slice(0, index + 1));
          index++;
        } else {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
          }
          updateAiWriterDone('true', { expires: addYears(new Date(), 1) });
          setIsAnimating(false);
        }
      }, writeSpeed);
    }, initialDelay);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [aiWriterDone, fullText, defaultAiSearchSet, updateAiWriterDone]);

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setText(newValue);
    setAiSearchState((prevState) => ({
      ...prevState,
      prompt: newValue,
      response: null,
      uuid: '',
    }));
  };

  return (
    <VStack spacing={4} align="stretch">
      <Box
        className={styles.field}
        position="relative"
        data-testid={`ai-filter-animation-${isAnimating ? 'running' : 'complete'}`}
      >
        <Textarea
          spellCheck={!showingHighlightedOverlay}
          ref={textAreaRef}
          value={isAnimating ? text : aiSearchState.prompt}
          isDisabled={isAiSearchLoading}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onChange={handleTextChange}
          placeholder={
            !defaultAiSearchSet ? getPlaceholderText(revelioPageId) : ''
          }
          height="65px"
          flex={1}
          colorScheme="green"
          width="475px"
          resize="none"
          padding="8px 6px"
          style={{
            color: showingHighlightedOverlay
              ? 'transparent'
              : 'var(--ai-search-fg, #2D426A)',
            caretColor: 'var(--ai-search-fg, #2D426A)',
          }}
          {...COMMON_AI_TEXT_STYLES}
          data-testid="ai-search-textarea"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSubmit();
            }
          }}
        />
        <Box position="absolute" right="1px" bottom="1px" zIndex={1}>
          <SpeechInput
            ref={speechInputRef}
            idleIconColor="var(--ai-search-fg, #2D426A)"
            onTranscriptComplete={handleTranscriptComplete}
            onEnter={handleSubmit}
            onStartListening={() => {
              setAiSearchState((prevState) => ({
                ...prevState,
                prompt: '',
                response: null,
                uuid: '',
              }));
            }}
            textAreaRef={textAreaRef}
          />
        </Box>
        {showingHighlightedOverlay &&
          aiSearchState.response &&
          'expr' in aiSearchState.response.filters && (
            <Box
              cursor="text"
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              overflow="auto"
              padding="9px 7px"
              onClick={(e) => {
                if (e.target === e.currentTarget && textAreaRef.current) {
                  textAreaRef.current.focus();
                  const endPosition = aiSearchState.prompt.length;
                  textAreaRef.current.setSelectionRange(
                    endPosition,
                    endPosition
                  );
                }
              }}
              {...COMMON_AI_TEXT_STYLES}
            >
              <ResponseHighlightedOverlay
                prompt={text}
                response={
                  aiSearchState.response as TalentDiscoveryAiFilterSearchResponseV2
                }
                textAreaRef={textAreaRef}
              />
            </Box>
          )}
      </Box>
      <Flex justifyContent="flex-end" alignItems="center">
        <ButtonGroup style={{ gap: 'var(--ui-spacing-4)' }}>
          {!defaultAiSearchSet && (
            <Button
              variant="link"
              size="sm"
              className={styles.linkButton}
              onClick={closeMenuAndStopRecording}
            >
              Cancel
            </Button>
          )}
          {defaultAiSearchSet && (
            <Button
              variant="link"
              size="sm"
              className={styles.linkButton}
              onClick={closeMenuAndStopRecording}
            >
              Explore Results
            </Button>
          )}
          <Button
            data-testid="ai-search-submit"
            disabled={!aiSearchState.prompt}
            color="primary"
            size="sm"
            onClick={handleSubmit}
          >
            {defaultAiSearchSet && aiSearchState.uuid
              ? 'Try your own search'
              : 'Search'}
          </Button>
        </ButtonGroup>
      </Flex>
    </VStack>
  );
};

const COMMON_AI_TEXT_STYLES: HTMLChakraProps<'div'> &
  HTMLChakraProps<'textarea'> = {
  fontSize: '14px',
  lineHeight: '17px',
};
